import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { RichText } from "@/components/resources/RichText";
import { ReadingProgress, ShareButtons } from "@/components/site/ReadingTools";
import { articles, resourcePath, type Block } from "@/data/resources";
import { products } from "@/data/products";
import { SITE_URL, seoForPath, useSeo } from "@/lib/seo";
import NotFound from "./NotFound";

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BlockView = ({ b }: { b: Block }) => {
  switch (b.type) {
    case "p":
      return <p className="text-muted-foreground leading-relaxed"><RichText text={b.text} /></p>;
    case "h3":
      return <h3 className="pt-2 font-display text-lg font-semibold text-foreground">{b.text}</h3>;
    case "ul":
      return (
        <ul className="space-y-2.5 pl-5 list-disc marker:text-primary text-muted-foreground leading-relaxed">
          {b.items.map((it, i) => <li key={i}><RichText text={it} /></li>)}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr>{b.head.map((h, i) => <th key={i} className="px-4 py-3 text-left font-semibold text-foreground">{h}</th>)}</tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {r.map((c, j) => <td key={j} className={`px-4 py-3 align-top ${j === 0 ? "font-medium text-foreground" : "text-muted-foreground"}`}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "diagram":
      return (
        <figure className="rounded-2xl border border-border bg-card p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-1">
            {b.steps.map((s, i) => (
              <div key={i} className="flex flex-col md:flex-row items-center gap-2 md:gap-1 md:flex-1">
                <div className="w-full rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 text-center text-sm font-medium text-foreground">{s}</div>
                {i < b.steps.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 rotate-90 md:rotate-0 text-primary" aria-hidden />}
              </div>
            ))}
          </div>
          <figcaption className="mt-4 text-center text-xs text-muted-foreground">{b.caption}</figcaption>
        </figure>
      );
  }
};

const ArticleContent = ({ slug }: { slug: string }) => {
  const a = articles.find((x) => x.slug === slug)!;
  useSeo(seoForPath(resourcePath(slug)));
  const product = products.find((p) => p.slug === a.product.slug)!;
  const related = a.related.map((s) => articles.find((x) => x.slug === s)!).filter(Boolean);
  const url = SITE_URL + resourcePath(slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: a.title,
      description: a.metaDescription,
      mainEntityOfPage: url,
      author: { "@type": "Organization", name: "Bane Tech", url: SITE_URL },
      publisher: { "@type": "Organization", name: "Bane Tech", url: SITE_URL, logo: { "@type": "ImageObject", url: `${SITE_URL}/banetech-logo.png` } },
      about: a.category,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Bane Tech", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/resources/` },
        { "@type": "ListItem", position: 3, name: a.title, item: url },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <ReadingProgress />
      <Navbar />
      <main>
        <article>
          <header className="pt-28 pb-10 md:pt-36 md:pb-14 bg-hero-network">
            <div className="container max-w-3xl">
              <nav aria-label="Breadcrumb" className="border-b border-border/70 pb-5 mb-10">
                <a href="/resources/" className="group inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                  All resources
                </a>
              </nav>
              <p className="font-mono text-xs uppercase tracking-widest text-primary">{a.category}</p>
              <h1 className="mt-4 font-display text-3xl md:text-5xl font-bold leading-tight tracking-tight">{a.title}</h1>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Bane Tech Engineering · {a.readingMinutes} min read
                </p>
                <ShareButtons url={url} title={a.title} />
              </div>
              <p className="mt-8 text-lg leading-relaxed text-foreground/90 border-l-2 border-primary pl-5">{a.intro}</p>
            </div>
          </header>

          <div className="container max-w-3xl py-12 space-y-12">
            {a.sections.map((s) => (
              <section key={s.heading} aria-labelledby={slugify(s.heading)} className="space-y-4">
                <h2 id={slugify(s.heading)} className="font-display text-2xl md:text-3xl font-semibold">{s.heading}</h2>
                {s.blocks.map((b, i) => <BlockView key={i} b={b} />)}
              </section>
            ))}

            <aside className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5 p-7 flex flex-col sm:flex-row gap-5 sm:items-center">
              <div className={`inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${product.accent}`}>
                <img src={product.icon} alt={product.alt} width={36} height={36} className="h-9 w-9" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">{a.product.heading}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed"><RichText text={a.product.text} /></p>
              </div>
            </aside>

            {related.length > 0 && (
              <section aria-labelledby="related" className="pt-4">
                <h2 id="related" className="font-display text-xl font-semibold mb-5">Related concepts</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {related.map((r) => (
                    <a key={r.slug} href={resourcePath(r.slug)} className="group rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
                      <p className="font-mono text-xs text-muted-foreground">{r.category}</p>
                      <p className="mt-1.5 font-medium group-hover:text-primary transition-colors">{r.title}</p>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

const ResourceArticlePage = () => {
  const { slug } = useParams();
  if (!slug || !articles.some((a) => a.slug === slug)) return <NotFound />;
  return <ArticleContent slug={slug} />;
};

export default ResourceArticlePage;
